import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Loader2, Plus, X } from 'lucide-react'
import { normalizeForSearch } from '../../utils/fuzzy'

/**
 * Choix de catégories : un combobox à jetons en autocomplétion.
 *
 * Écrit une fois pour DEUX écrans du builder — les catégories d'un média dans
 * la médiathèque, et celles d'une page dans le bandeau de l'éditeur. Il ne
 * connaît ni entité, ni vocabulaire, ni site : tout passe par `onSearch` /
 * `onCreate`, que l'appelant branche sur son propre point d'API.
 *
 * Le champ est multivalué, donc ce n'est pas un menu déroulant mais un
 * **combobox à jetons** : ce qui est déjà choisi s'affiche en jetons
 * retirables, la saisie filtre le reste, et une catégorie absente se crée à la
 * volée depuis la même saisie (c'est le seul endroit où l'on en crée — un
 * second formulaire ailleurs ferait deux chemins pour le même geste).
 *
 * Le motif ARIA « combobox » est suivi à la lettre parce que rien d'autre ne
 * rend ce composant utilisable au clavier : `role="combobox"` sur la saisie,
 * `aria-expanded`, `aria-controls` vers la liste, et `aria-activedescendant`
 * qui désigne l'option survolée SANS déplacer le focus — c'est ce qui permet
 * de continuer à taper pendant qu'on parcourt les suggestions.
 *
 * Props :
 *  - value    : [{id, label}] — les catégories du média
 *  - onSearch : (q, {signal}) => Promise<[{id, label}]> — interrogé À LA
 *    FRAPPE (débouncé). Le vocabulaire n'est jamais chargé en entier : il
 *    peut compter des centaines de termes, dont on n'affiche que vingt.
 *  - disabled : bool
 *  - onChange : (ids: number[]) => void
 *  - onCreate : (label: string) => Promise<{id, label} | null>
 *  - inputId  : l'id du champ, quand l'appelant pose son propre <label for>.
 *    À défaut, un id unique est engendré — deux instances sur un même écran ne
 *    doivent pas partager le leur.
 *  - inline   : disposition sur UNE ligne (jetons et saisie côte à côte), pour
 *    une barre d'outils. Par défaut la disposition est empilée, celle d'un
 *    panneau de formulaire.
 *  - placeholder : le texte du champ vide (défaut : « Rechercher ou créer… »).
 *  - ariaLabel : le nom accessible quand il n'y a pas de <label> visible.
 */

/** Délai avant d'interroger le serveur. Assez court pour ne pas se sentir,
 *  assez long pour qu'une frappe normale ne fasse qu'une requête. */
const SEARCH_DEBOUNCE_MS = 200

export const CategoryAutocomplete = ({
  value = [],
  disabled = false,
  onChange,
  onCreate,
  onSearch,
  inputId,
  inline = false,
  placeholder,
  ariaLabel,
}) => {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [creating, setCreating] = useState(false)
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)

  const rootRef = useRef(null)
  const inputRef = useRef(null)
  // Garde-fou contre une réponse lente qui écraserait une plus récente.
  const ticketRef = useRef(0)
  const listId = useId()
  const optionId = (i) => `${listId}-option-${i}`
  const fieldId = inputId || `${listId}-input`

  const selectedIds = useMemo(() => new Set(value.map(v => v.id)), [value])

  // Les suggestions viennent du SERVEUR, débouncées. La liste n'est
  // interrogée que lorsqu'elle est ouverte : inutile de solliciter la base
  // pour un champ auquel personne ne touche.
  useEffect(() => {
    if (!open || typeof onSearch !== 'function') return undefined
    const controller = new AbortController()
    const ticket = ++ticketRef.current
    setSearching(true)
    const timer = setTimeout(async () => {
      const found = await onSearch(query.trim(), { signal: controller.signal })
      if (ticket !== ticketRef.current) return
      setResults(found)
      setSearching(false)
    }, SEARCH_DEBOUNCE_MS)
    return () => { clearTimeout(timer); controller.abort() }
  }, [open, query, onSearch])

  // Le serveur ignore ce que le média porte déjà : on l'écarte ici, sinon on
  // proposerait d'ajouter une catégorie déjà posée.
  const suggestions = useMemo(
    () => results.filter(o => !selectedIds.has(o.id)),
    [results, selectedIds],
  )

  // « Créer » n'apparaît que si la saisie ne correspond à RIEN d'existant —
  // ni à une suggestion, ni à une catégorie déjà posée sur ce média.
  const trimmed = query.trim()
  const known = [...results, ...value]
  const exists = trimmed !== '' && known.some(o => normalizeForSearch(o.label) === normalizeForSearch(trimmed))
  const canCreate = trimmed !== '' && !searching && !exists && typeof onCreate === 'function'

  const rows = canCreate ? [...suggestions, { create: true }] : suggestions

  useEffect(() => { setActiveIndex(0) }, [query, open])

  // Refermer sur un clic à l'extérieur — pas sur `blur`, qui se déclenche
  // aussi quand on clique une option et empêcherait de la choisir.
  useEffect(() => {
    if (!open) return undefined
    const onPointerDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  const commit = (ids) => onChange?.(ids)

  const add = (category) => {
    if (!category || selectedIds.has(category.id)) return
    commit([...value.map(v => v.id), category.id])
    setQuery('')
    setOpen(false)
    inputRef.current?.focus()
  }

  const remove = (id) => {
    commit(value.filter(v => v.id !== id).map(v => v.id))
    inputRef.current?.focus()
  }

  const create = async () => {
    if (!canCreate || creating) return
    setCreating(true)
    const created = await onCreate(trimmed)
    setCreating(false)
    if (created) {
      setResults(prev => (prev.some(o => o.id === created.id) ? prev : [...prev, created]))
      add(created)
    }
  }

  const choose = (index) => {
    const row = rows[index]
    if (!row) return
    if (row.create) create()
    else add(row)
  }

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!open) { setOpen(true); return }
      if (!rows.length) return
      const delta = e.key === 'ArrowDown' ? 1 : -1
      setActiveIndex((i) => (i + delta + rows.length) % rows.length)
      return
    }
    if (e.key === 'Enter') {
      if (!open || !rows.length) return
      // Sans ceci, la touche validerait le formulaire parent (ou la pop-in).
      e.preventDefault()
      choose(activeIndex)
      return
    }
    if (e.key === 'Escape') {
      if (open) { e.stopPropagation(); setOpen(false) }
      return
    }
    // Retour arrière sur une saisie vide : on retire le dernier jeton, comme
    // dans tous les champs à jetons.
    if (e.key === 'Backspace' && query === '' && value.length) {
      remove(value[value.length - 1].id)
    }
  }

  return (
    <div className={inline ? 'media-lib-ac media-lib-ac--inline' : 'media-lib-ac'} ref={rootRef}>
      {value.length > 0 && (
        <ul className="media-lib-ac-tags">
          {value.map(cat => (
            <li key={cat.id} className="media-lib-ac-tag">
              <span className="truncate">{cat.label}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => remove(cat.id)}
                  aria-label={`Retirer la catégorie ${cat.label}`}
                  className="media-lib-ac-tag-remove"
                >
                  <X size={11} aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="media-lib-ac-field">
        <input
          ref={inputRef}
          id={fieldId}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && rows.length ? optionId(activeIndex) : undefined}
          aria-label={ariaLabel}
          disabled={disabled}
          value={query}
          placeholder={placeholder ?? (value.length ? 'Ajouter une catégorie…' : 'Rechercher ou créer une catégorie…')}
          onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          className="input media-lib-ac-input"
        />

        {open && (
          <ul id={listId} role="listbox" aria-label="Catégories" className="media-lib-ac-list">
            {searching && rows.length === 0 && (
              <li className="media-lib-ac-empty">Recherche…</li>
            )}
            {!searching && rows.length === 0 && (
              <li className="media-lib-ac-empty">Aucune catégorie ne correspond.</li>
            )}
            {rows.map((row, i) => (
              <li
                key={row.create ? '__create' : row.id}
                id={optionId(i)}
                role="option"
                aria-selected={i === activeIndex}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseDown={(e) => { e.preventDefault(); choose(i) }}
                className="media-lib-ac-option"
                data-active={i === activeIndex ? 'true' : undefined}
              >
                {row.create ? (
                  <>
                    {creating
                      ? <Loader2 size={13} className="animate-spin" aria-hidden="true" />
                      : <Plus size={13} aria-hidden="true" />}
                    <span>Créer « {trimmed} »</span>
                  </>
                ) : (
                  <span className="truncate">{row.label}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <p aria-live="polite" className="sr-only">
        {open && !searching ? `${rows.length} suggestion${rows.length > 1 ? 's' : ''}` : ''}
      </p>
    </div>
  )
}
